import { useState, useEffect } from 'react';
import { getClientProfile } from '../services/clientProfileService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to fetch client profile data
 * Now uses session.client_id from AuthContext by default
 *
 * @deprecated Do NOT use the `plan` field from this hook - it's deprecated.
 * Use the `usePlan()` hook from `@/utils/planUtils` instead,
 * which reads from entitlement.plan_id as the single source of truth.
 *
 * This hook should only be used for other profile data like:
 * - organization_name, organization_type
 * - beds, annual_revenue
 * - survey responses, etc.
 *
 * @param {string} [clientIdOverride] - Optional clientId override (defaults to session.client_id)
 * @returns {{ data: Object|null, isLoading: boolean, error: Error|null, refetch: Function }}
 */
export function useClientProfile(clientIdOverride = null) {
  const { session } = useAuth();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Use override if provided, otherwise use session.client_id
  const clientId = clientIdOverride || session?.client_id;

  // Debug logging
  console.log('🔍 useClientProfile called:', {
    clientIdOverride,
    sessionClientId: session?.client_id,
    effectiveClientId: clientId,
  });

  useEffect(() => {
    if (!clientId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const profile = await getClientProfile(clientId);
        
        if (!cancelled) {
          setData(profile);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching client profile:', err);
          setError(err);
          setIsLoading(false);
        }
      }
    };

    fetchProfile();

    return () => {
      cancelled = true;
    };
  }, [clientId, refetchTrigger, session?.client_id]);

  const refetch = () => setRefetchTrigger(prev => prev + 1);

  return { data, isLoading, error, refetch };
}